import React, { useState, useEffect } from "react";
import moment from "moment";
import { Grid, FormControl, Checkbox, Box, FormGroup, FormControlLabel, FormLabel, Radio, RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography, Autocomplete, TextField, Button, Tooltip, InputAdornment, TableContainer } from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { rangeRows, rateTypes } from "..";
import { regexConstants } from "../../../constants/regEx";
import { numberWithCommas } from "../../../utils";
import { NoRecordFound } from "../../../shared_elements";
import { backendDateFormat, fieldDateFormat } from "../../../constants";
import { trackActivity } from "../../../utils/mixpanel";
import { RangeContinuityInfo } from "../../elements";
import { exportToExcel, formatExcelDate, validateExcelDate } from "../../../utils/helper";
import ImportModal from "../../elements/ImportModal";
import FixedTableConditions from "./FixedTableConditions";
const rangeConditionMap = {
  "less than equal to": {
    value: 1,
    label: "Less Than Equal To",
  },
  "in between equal to": {
    value: 2,
    label: "In Between Equal To",
  },
  "greater than equal to": {
    value: 3,
    label: "Greater Than Equal To",
  },
};
export default function RangeConditionTable({ data, onFieldChange, pbhErrors, resetPBHError, modeType, setError, error, onImportExcel, excelData, setExcelData }) {
  const [fhConditions, setFhConditions] = useState([]);
  const [fcConditions, setFcConditions] = useState([]);
  const [prevUnitValue, setPrevUnitValue] = useState(null);
  const [rangeContinuity, setRangeContinuity] = useState(false);
  const [importDetails, setImportDetails] = useState({ modal: false, data: null })
  const rentalRateBasedOn = data?.rental_rate_based_on?.value
  const isFcFH = data?.unit_of_measurement?.value === 4;
  const rateByMap = { 1: "Flight Hours", 2: "Flight Cycles", 3: "Block Hours", 8: data?.interval?.label ? data?.interval?.label : "" };
  const exportNameMap = { "FH": "Flight_Hours", "FC": "Flight_Cycles", "BH": "Block_Hours", "Ratio Table": data?.interval?.value === 2 ? "Flight_Hours" : "Flight_Cycles" };
  const rateTypeMap = { "FIXED": { label: "FIXED", value: 1 }, "PER UNIT": { label: "PER UNIT", value: 2 }, };
  const requiredHeaders = rentalRateBasedOn !== 8 ? ["range conditions", "min", "max", "rate type", "rate",] : ["range conditions", "min", "max", "rate",];
  const unitValue = { FH: 1, FC: 2, }[importDetails?.data] || 3;
  const headerMap = {
    "range condition": "range conditions",
    "range conditions": "range conditions",
    "min": "min",
    "minimum": "min",
    "max": "max",
    "maximum": "max",
    "rate": "rate",
    [`rate/${data?.interval?.label?.toLowerCase()}`]: "rate",
    [`rate/${rateByMap[rentalRateBasedOn]?.toLowerCase()}`]: "rate",
    [`rate/${rateByMap[data?.unit_of_measurement?.value]?.toLowerCase()}`]: "rate",
    [`rate/${rateByMap[unitValue]?.toLowerCase()}`]: "rate",
    "rate type": "rate type",
    "rental rate type": "rate type",
    "escalation": "escalation (%)",
    "escalation (%)": "escalation (%)",
    "esc start": "esc start",
  };
  const isButtonEnabled = rentalRateBasedOn === 8 ? !data?.interval?.value || !data?.allow_decimal?.value || !data?.rounding?.value : false;
  const samplePayload = { rental_rate_based_on: rentalRateBasedOn === 8 ? 8 : { 1: 2, 2: 3, 3: 6, }[unitValue] || unitValue, ...(rentalRateBasedOn === 8 && { interval: data?.interval?.value || null, }), };
  useEffect(() => {
    const currentUnitValue = data?.unit_of_measurement?.value;
    if (prevUnitValue !== null && prevUnitValue !== currentUnitValue) {
      setFhConditions([]);
      setFcConditions([]);
      // onFieldChange("pbh_conditions", []);
    }
    setPrevUnitValue(currentUnitValue);
    const conditions = data?.rental_type?.value === 4 && data?.rental_rate_based_on?.value === 8 ? data?.interval_condition : data?.pbh_conditions || [];
    if (currentUnitValue === 4) {
      const fh = conditions?.filter(row => row.unit_of_measure_for?.value === 1)?.map(prepRow);
      const fc = conditions?.filter(row => row.unit_of_measure_for?.value === 2)?.map(prepRow);
      setFhConditions(fh);
      setFcConditions(fc);
    } else if (currentUnitValue === 2) {
      const updated = conditions.map(row => {
        const updatedRow = prepRow(row);
        updatedRow.unit_of_measure_for = { value: 2, label: "FC" };
        return updatedRow;
      });
      setFcConditions(updated);
      setFhConditions([]);
    } else {
      const updated = conditions.map(row => {
        const updatedRow = prepRow(row);
        updatedRow.unit_of_measure_for = data.unit_of_measurement;
        return updatedRow;
      });
      setFhConditions(updated);
      setFcConditions([]);
    }

  }, [data?.pbh_conditions, data?.interval_condition, data?.rental_rate_based_on?.value, data?.unit_of_measurement?.value]);
  const prepRow = (row) => {
    const newRow = { ...row };
    if (row.range_condition?.value === 1) newRow.min_value = "0";
    return newRow;
  };

  const addRowFromRange = (existingList, unitLabel, unitValue) => {
    const defaultRateType = rateTypes.find(r => r.value === 1);
    const usedConditions = existingList?.map(row => row.range_condition?.value);
    if (!usedConditions.includes(1)) {
      const row = rangeRows.find(r => r.range_condition.value === 1);
      return [
        ...existingList,
        { ...row, unit_of_measure_for: { value: unitValue, label: unitLabel }, rate_type: defaultRateType }
      ];
    }
    if (!usedConditions.includes(3)) {
      const row = rangeRows.find(r => r.range_condition.value === 3);
      return [
        ...existingList,
        { ...row, unit_of_measure_for: { value: unitValue, label: unitLabel }, rate_type: defaultRateType }
      ];
    }
    const inBetweenRow = rangeRows.find(r => r.range_condition.value === 2);
    const newRow = {
      ...inBetweenRow,
      unit_of_measure_for: { value: unitValue, label: unitLabel },
      rate_type: defaultRateType
    };
    const greaterIndex = existingList.findIndex(row => row.range_condition?.value === 3);
    if (greaterIndex === -1) { return [...existingList, newRow]; }
    const updated = [...existingList];
    updated.splice(greaterIndex, 0, newRow);
    return updated;
  };

  const handleAddRow = (type) => {
    const rangeConditionErrors = {};
    const rangeConditionFields = [];
    if (!data?.interval) {
      rangeConditionErrors.interval = "Please select Rental Rate interval";
      rangeConditionFields.push("Rental Rate Interval");
    }
    if (!data?.allow_decimal) {
      rangeConditionErrors.allow_decimal = "Please select Allow Decimal";
      rangeConditionFields.push("Allow Decimal");
    }
    if (!data?.rounding) {
      rangeConditionErrors.rounding = "Please select Rounding";
      rangeConditionFields.push("Rounding");
    }
    if (rangeConditionFields.length > 0 && data?.rental_rate_based_on?.value === 8) {
      setError(prev => ({ ...prev, ...rangeConditionErrors }));
      return;
    }
    let currentList = [];
    let setList;
    let unitLabel, unitValue;

    switch (type) {
      case "FH":
        currentList = fhConditions;
        setList = setFhConditions;
        unitLabel = "FH";
        unitValue = 1;
        break;
      case "FC":
        currentList = fcConditions;
        setList = setFcConditions;
        unitLabel = "FC";
        unitValue = 2;
        break;
      case "BH":
        currentList = fhConditions;
        setList = setFhConditions;
        unitLabel = "BH";
        unitValue = 3;
        break;
      case "Ratio Table":
        currentList = fhConditions;
        setList = setFhConditions;
        unitLabel = "Ratio Table";
        unitValue = 8;
        break;
      default:
        return;
    }

    const updated = addRowFromRange(currentList, unitLabel, unitValue);
    setList(updated);
    const currentUnit = data?.unit_of_measurement?.value;
    if (currentUnit === 4) {
      updateAllConditions(
        type === "FC" ? fhConditions : updated,
        type === "FC" ? updated : fcConditions
      );
    } else if (currentUnit === 2) {
      updateAllConditions([], updated);
    } else {
      updateAllConditions(updated, []);
    }
  };
  const handleDeleteRow = (index, type) => {
    const isFH = type === "FH" || type === "BH" || type === "Ratio Table";
    const updated = (isFH ? fhConditions : fcConditions)?.filter((_, i) => i !== index);
    if (isFcFH) {
      if (type === "FC") {
        updateAllConditions(fhConditions, updated);
      } else {
        updateAllConditions(updated, fcConditions);
      }
    } else {

      if (type === "FC") {
        updateAllConditions([], updated);
      } else {
        updateAllConditions(updated, []);
      }
    }
  };

  const handleRowChange = (index, field, value, type) => {
    const isFH = type === "FH" || type === "BH" || type === "Ratio Table";
    const updated = [...(isFH ? fhConditions : fcConditions)];
    const currentRow = updated[index] || {};
    const updatedRow = {
      ...currentRow,
      [field]: value,
      unit_of_measure_for: currentRow.unit_of_measure_for || { value: type === "FH" ? 1 : type === "FC" ? 2 : 3, label: type, },
    };
    if (field === "range_condition" && value?.value === 1) {
      updatedRow.min_value = "0";
    }
    if (field === "range_condition" && value?.value === 2) {
      updatedRow.max_value = "0";
    }
    updated[index] = updatedRow;
    if (isFH) {
      setFhConditions(updated);
      updateAllConditions(updated, fcConditions);
    } else {
      setFcConditions(updated);
      updateAllConditions(fhConditions, updated);
    }
  };

  const updateAllConditions = (fh, fc) => {
    const currentUnit = data?.unit_of_measurement?.value;
    let all = [];
    if (currentUnit === 4) { all = [...fh, ...fc]; } else if (currentUnit === 2) { all = [...fc]; } else { all = [...fh]; }
    onFieldChange(data?.rental_type?.value === 4 && data?.rental_rate_based_on?.value === 8 ? "interval_condition" : "pbh_conditions", all);
  };
  const handleItemClicked = (page_type) => { trackActivity('Item Clicked', { page_title: `Range Conditions`, page_type: page_type, }) }
  const validateContinuousRanges = (ranges) => {
    const normalize = (val) => val === undefined || val === null || val === "" ? null : Number(parseFloat(val).toFixed(2));
    const formatted = ranges.map((r, i) => ({ index: i, min: normalize(r.min_value), max: normalize(r.max_value), })).sort((a, b) => (a.min ?? 0) - (b.min ?? 0));
    for (let i = 0; i < formatted.length - 1; i++) {
      const current = formatted[i];
      const next = formatted[i + 1];
      if (current.max == null || next.min == null) continue;
      const diff = Number((next.min - current.max).toFixed(2));
      if (diff < 0) { return true; }
      if (diff > 0.01) { return true; }
    }
    return false;
  };

  useEffect(() => {
    const newContinuityState = {};
    const unitValue = data?.unit_of_measurement?.value;
    if (unitValue === 4) {
      if (fhConditions?.length > 0) { newContinuityState["FH"] = validateContinuousRanges(fhConditions); }
      if (fcConditions?.length > 0) { newContinuityState["FC"] = validateContinuousRanges(fcConditions); }
    }
    else if (unitValue === 2) {
      if (fcConditions?.length > 0) { newContinuityState["FC"] = validateContinuousRanges(fcConditions); }
    }
    else {
      const label = data?.rental_rate_based_on?.value === 8 ? "Ratio Table" : getLabelFromValue(unitValue);
      if (fhConditions?.length > 0) { newContinuityState[label] = validateContinuousRanges(fhConditions); }
    }
    setRangeContinuity(newContinuityState);
  }, [fhConditions, fcConditions, data?.unit_of_measurement?.value, data?.rental_rate_based_on?.value]
  );

  const pbhTable = (PbhInfo, label) => {
    const rateBy = data?.interval?.label ? data?.interval?.label : data?.unit_of_measurement?.value ? data?.unit_of_measurement?.value === 4 ? label === "FH" ? "Flight Hours" : "Flight Cycles" : rateByMap[data?.unit_of_measurement?.value] : ""
    return (
      <Box mt={0.4}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center" gap={0.2}>
            <Typography className='section-header-text'>  {label === "Ratio Table" ? "Ratio Table" : `FOR ${label}`}</Typography>
            <Tooltip placement='left' slotProps={{ tooltip: { sx: { bgcolor: "white", color: "black", boxShadow: 2, border: "1px solid #ddd" } }, arrow: { sx: { color: "white" } } }} title={<RangeContinuityInfo />} arrow > <InfoIcon fontSize='small' color='primary' /> </Tooltip>
            <span style={{ display: "flex", alignItems: "center", marginTop: "-5px" }}>:</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", }}>
            <>
              <Button disabled={isButtonEnabled || modeType === "view"} onClick={() => { setImportDetails({ modal: true, data: label }); handleItemClicked('Import Button - RangeConditionTable') }} >Import</Button>
              <Button sx={{ pointerEvents: !data?.pbh_conditions?.length ? "not-allowed" : "pointer" }} disabled={!PbhInfo?.length || isButtonEnabled}
                onClick={() => {
                  handleItemClicked('Export Button - RangeConditionTable'); exportToExcel({
                    data: PbhInfo,
                    fileName: `${rentalRateBasedOn === 8 ? data?.interval?.value === 2 ? "Flight_Hours" : "Flight_Cycles" : label}_${rentalRateBasedOn === 8 ? "Range_Based_Ratio" : "Based_Rate"}`,
                    sheetName: `${rentalRateBasedOn !== 8 ? "Rate Table" : "Ratio Table"}`,
                    columns: {
                      "Range Conditions": (row) => row?.range_condition?.label || "",
                      "Min": "min_value",
                      "Max": (row) => (row?.range_condition?.value === 3 && (row.max_value === 0 || row.max_value === "0") ? "∞" : row.max_value),
                      ...(rentalRateBasedOn !== 8 && { "Rate Type": (row) => row?.rate_type?.label || "", }),
                      [`Rate${rateBy ? `/${rateBy}` : ''}`]: (row) => `${data?.currency?.symbol || "$"} ${row.rate}`,
                      ...(data?.table_based_esc && { "Escalation (%)": "esc_percentage", "Esc Start": (row) => row.esc_date ? moment(row.esc_date).format("DD-MM-YYYY") : null, }),
                    }
                  })
                }}>Export</Button>
            </>
          </Box>
        </Box>
        <TableContainer component={Paper} className="scrollable-table rental-scrollable-table-container">
          <Paper className="lease-detail-table">
            <Table className="mui-table-format">
              <TableHead>
                <TableRow>
                  <TableCell>Range Conditions</TableCell>
                  <TableCell>MIN*</TableCell>
                  <TableCell>MAX*</TableCell>
                  {data?.rental_rate_based_on?.value !== 8 && <TableCell>Rate Type*</TableCell>}
                  <TableCell>{`Rate${rateBy ? `/${rateBy}` : ''}*`}</TableCell>
                  <TableCell>Escalation{data?.table_based_esc ? "*" : ""}</TableCell>
                  <TableCell>Esc Start{data?.table_based_esc ? "*" : ""}</TableCell>
                  {modeType !== 'view' ?
                    <TableCell className='sticky-action-header' align="center">Action</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {PbhInfo?.length > 0 && PbhInfo.map((pbh, index) => {
                  const globalIndex = label === "FC" ? fhConditions.length + index : index;
                  return (
                    <TableRow key={index}>
                      <TableCell>{pbh?.range_condition?.label ? pbh?.range_condition?.label : pbh?.range_condition}</TableCell>
                      <TableCell>
                        <TextField
                          id="min_value"
                          size="small"
                          variant="outlined"
                          slotProps={{ inputLabel: { shrink: true }, }}
                          value={(() => { const rawValue = pbh?.range_condition?.value === 1 ? "0" : pbh?.min_value === 0 ? "0" : pbh?.min_value ? pbh.min_value : ''; return modeType === 'view' && rawValue !== '' ? numberWithCommas(rawValue) : rawValue; })()}
                          disabled={pbh.range_condition?.value === 1 || modeType === 'view'}
                          onFocus={() => resetPBHError(globalIndex, "min_value")}
                          error={!!pbhErrors?.[globalIndex]?.min_value}
                          helperText={pbhErrors?.[globalIndex]?.min_value}
                          onChange={(e) => regexConstants.decimalnodotstart.test(e.target.value) ? handleRowChange(index, "min_value", e.target.value, label) : e.preventDefault()}
                        // onBlur={() => {
                        //   const isContinuityValid = validateContinuousRanges(PbhInfo);
                        //   setRangeContinuity(prev => ({
                        //     ...prev,
                        //     [label]: isContinuityValid
                        //   }));
                        // }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          id="max_value"
                          size="small"
                          variant="outlined"
                          slotProps={{ inputLabel: { shrink: true }, }}
                          value={(() => { const rawValue = pbh?.range_condition?.value === 3 ? "∞" : pbh?.max_value === 0 ? "0" : pbh?.max_value ? pbh.max_value : ''; return modeType === 'view' && rawValue !== '' ? numberWithCommas(rawValue) : rawValue; })()}
                          disabled={pbh.range_condition?.value === 3 || modeType === 'view'}
                          onFocus={() => resetPBHError(globalIndex, "max_value")}
                          error={!!pbhErrors?.[globalIndex]?.max_value}
                          helperText={pbhErrors?.[globalIndex]?.max_value}
                          onChange={(e) => regexConstants.decimalnodotstart.test(e.target.value) ? handleRowChange(index, "max_value", e.target.value, label) : e.preventDefault()}
                        // onBlur={() => {
                        //   const isContinuityValid = validateContinuousRanges(PbhInfo);
                        //   setRangeContinuity(prev => ({
                        //     ...prev,
                        //     [label]: isContinuityValid
                        //   }));
                        // }}
                        />
                      </TableCell>
                      {data?.rental_rate_based_on?.value !== 8 && <TableCell>
                        <Autocomplete
                          options={rateTypes}
                          getOptionLabel={(option) => option.label}
                          value={rateTypes.find((type) => type.value === (pbh?.rate_type?.value ?? 1)) || null}
                          disableClearable
                          className='custom-autocomplete'
                          disabled={modeType === 'view'}
                          onChange={(e, newValue) => handleRowChange(index, "rate_type", newValue, label)}
                          style={{ width: "170px" }}
                          onFocus={() => resetPBHError(globalIndex, "rate_type")}
                          renderInput={(params) => (<TextField required slotProps={{ inputLabel: { shrink: true } }} {...params} error={Boolean(!!pbhErrors?.[globalIndex]?.rate_type)} helperText={pbhErrors?.[globalIndex]?.rate_type} size="small" margin='normal' variant="outlined" />)}
                        />
                      </TableCell>}
                      <TableCell>
                        <TextField
                          size="small"
                          slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{data?.currency?.symbol}</InputAdornment>) } }}
                          variant="outlined"
                          value={(() => { const rawValue = pbh.rate === 0 ? '0' : pbh.rate || ''; return modeType === 'view' && rawValue !== '' ? numberWithCommas(rawValue) : rawValue; })()}
                          onFocus={() => resetPBHError(globalIndex, "rate")}
                          disabled={modeType === 'view'}
                          error={!!pbhErrors?.[globalIndex]?.rate}
                          helperText={pbhErrors?.[globalIndex]?.rate}
                          onChange={(e) => regexConstants.decimalnodotstart.test(e.target.value) ? handleRowChange(index, "rate", e.target.value, label) : e.preventDefault()}
                        />
                      </TableCell>
                      <TableCell >
                        <TextField
                          size="small"
                          slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { endAdornment: (<InputAdornment position="end">%</InputAdornment>) } }}
                          variant="outlined"
                          value={pbh.esc_percentage === 0 ? '0' : pbh.esc_percentage || ''}
                          onFocus={() => resetPBHError(globalIndex, "esc_percentage")}
                          disabled={modeType === 'view' || !data?.table_based_esc}
                          error={!!pbhErrors?.[globalIndex]?.esc_percentage}
                          helperText={pbhErrors?.[globalIndex]?.esc_percentage}
                          onChange={(e) => regexConstants.percantageRegExp.test(e.target.value) ? handleRowChange(index, "esc_percentage", e.target.value, label) : e.preventDefault()}
                        />
                      </TableCell>
                      <TableCell >
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            value={pbh?.esc_date ? moment(pbh?.esc_date) : null}
                            onChange={(data) => {
                              const isValid = moment(data)?.isValid();
                              const formattedDate = isValid ? moment(data).format(backendDateFormat) : null;
                              handleRowChange(index, "esc_date", formattedDate, label)
                            }}
                            slotProps={{
                              popper: { placement: 'bottom-end' },
                              textField: { required: true, error: !!pbhErrors?.[globalIndex]?.esc_date, helperText: pbhErrors?.[globalIndex]?.esc_date, InputLabelProps: { shrink: true }, size: "small", margin: "normal", onFocus: () => resetPBHError(globalIndex, "esc_date"), fullWidth: true, },
                              inputProps: { style: { padding: "8.5px 14px", textAlign: "left" } },
                            }}
                            format={fieldDateFormat}
                            disabled={modeType === 'view' || !data?.table_based_esc}
                            sx={{ width: "100%", minWidth: "160px", }}
                          />
                        </LocalizationProvider>
                      </TableCell>
                      {modeType !== 'view' ?
                        <TableCell align='center' sx={{ verticalAlign: "middle", p: 0 }}>
                          <Tooltip title="Remove">
                            <DeleteIcon color="error" fontSize="small" sx={{ cursor: "pointer", }} onClick={() => { handleDeleteRow(index, label); handleItemClicked('DeleteIcon') }} />
                          </Tooltip>
                        </TableCell> : null}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            {PbhInfo?.length === 0 && <NoRecordFound />}
          </Paper>
        </TableContainer>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          {modeType !== 'view' ?
            <Button variant="outlined" size="small" onClick={() => { handleAddRow(label); handleItemClicked('Add Row Button') }} > Add Range Conditions</Button>
            : null}
          {rangeContinuity[label] && <Box className="warning-text" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}> <WarningAmberIcon sx={{ fontSize: 16, color: "#a78701" }} /> It’s recommended to define ranges in a continuous sequence. Refer to the info icon. </Box>}
        </Box>
      </Box>
    )
  };

  const getLabelFromValue = (value) => {
    switch (value) {
      case 1: return "FH";
      case 2: return "FC";
      case 3: return "BH";
      case 8: return "Ratio Table";
      default: return "Unknown";
    }
  };

  const transformExcelData = (transformData) => {
    return transformData.map((row, index) => {
      const rateValue = String(row["rate"] || "").replace(/[^0-9.]/g, "");
      const rateTypeRaw = String(row["rate type"] || "").toUpperCase().trim();
      const rangeCondition = rangeConditionMap[String(row["range conditions"] || "").toLowerCase().trim()];
      return {
        range_condition: { value: rangeCondition?.value, label: row["range conditions"] },
        ...(rentalRateBasedOn !== 8 && {
          unit_of_measure_for: {
            label: importDetails?.data,
            value: importDetails?.data === "FH" ? 1 : importDetails?.data === "FC" ? 2 : 3
          },
        }),
        rate: Number(rateValue) || 0,
        esc_percentage: data?.table_based_esc ? Number(row["escalation (%)"] || 0) : 0,
        min_value: Number(row["min"]) || 0,
        max_value: row["max"] === "∞" ? 0 : Number(row["max"]) || 0,
        esc_date: data?.table_based_esc ? formatExcelDate(row["esc start"] || row["Esc Start"]) : null,
        ...(rentalRateBasedOn !== 8 && { rate_type: rateTypeMap[rateTypeRaw] || null, }),
        id: `pbh_${index + 1}_${rentalRateBasedOn || data?.unit_of_measurement?.value}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      };
    });
  };

  const validateExcelData = (rows) => {
    const isValidTwoDecimal = (value) => { return /^\d+(\.\d{1,2})?$/.test(String(value).trim()); };
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rate = String(row["rate"] || "").replace(/[^0-9.]/g, "");
      const rateTypeRaw = String(row["rate type"] || "").toUpperCase().trim();
      const escDate = row["esc start"] || row["Esc Start"];
      if (!row["range conditions"] || !rate || row["min"] === "" || row["max"] === "" || row["min"] === null || row["max"] === null) {
        return `Invalid data at row ${i + 2}. Please set format like sample file.`;
      }
      if (!isValidTwoDecimal(row["min"])) { return `Min at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (row["max"] !== "∞" && !isValidTwoDecimal(row["max"])) { return `Max at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (!isValidTwoDecimal(rate)) { return `Rate at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (data?.table_based_esc && !isValidTwoDecimal(row["escalation (%)"] || 0)) { return `Escalation (%) at row ${i + 2} accepts maximum 2 decimal places only.`; }
      if (rentalRateBasedOn !== 8 && !rateTypeMap[rateTypeRaw]) { return `Invalid or missing Rate Type at row ${i + 2}. Allowed: FIXED / PER UNIT`; }
      if (escDate && !validateExcelDate(escDate)) { return `Invalid Esc Start date at row ${i + 2}.`; }
    }
    return null;
  };
  return (
    <>
      {data?.rental_type?.value === 3 && data?.unit_of_measurement ? <FormControl className="radio-color" component="fieldset">
        <FormLabel component="legend" sx={{ fontSize: '14px' }}>Range Condition To Be Considered</FormLabel>
        <RadioGroup row defaultValue="either_off" value={data?.is_pbh_fixed_table ? "fixed" : data?.fixed_rate ? "fixed_rate" : (data?.range_condition_to_consider ? data?.range_condition_to_consider : "either_off")} name="rangeCondition">
          <FormControlLabel
            value="either_off"
            control={<Radio disableRipple disabled={modeType === 'view'} checked={!data?.is_pbh_fixed_table && !data?.fixed_rate && (data?.range_condition_to_consider ? data?.range_condition_to_consider === "either_off" : true)} onChange={() => onFieldChange("range_condition_to_consider", "either_off")} />}
            label="Either Off"
          />
          <FormControlLabel
            value="all"
            control={<Radio disableRipple disabled={modeType === 'view'} checked={!data?.is_pbh_fixed_table && !data?.fixed_rate && data?.range_condition_to_consider === "all"} onChange={() => onFieldChange("range_condition_to_consider", "all")} />}
            label="ALL"
          />
          {isFcFH ? <>
            <FormControlLabel
              value="fixed"
              control={<Radio disableRipple disabled={modeType === 'view'} checked={data?.is_pbh_fixed_table === true} onChange={() => onFieldChange("range_condition_to_consider", "fixed")} />}
              label="Fixed Table"
            />
            <FormControlLabel
              value="fixed_rate"
              control={<Radio disableRipple disabled={modeType === 'view'} checked={data?.fixed_rate === true} onChange={() => onFieldChange("range_condition_to_consider", "fixed_rate")} />}
              label="Fixed Rate"
            />
          </> : null}
        </RadioGroup>
      </FormControl> : null}
      <FixedTableConditions data={data} onFieldChange={onFieldChange} error={error} setError={setError} modeType={modeType} />
      {!data?.is_pbh_fixed_table && !data?.fixed_rate && ((data?.rental_type?.value === 3 && data?.unit_of_measurement) || data?.rental_rate_based_on?.value === 8) ?
        <Grid size={{ md: 12, xs: 12 }}>
          <FormGroup className='disabled-checkbox-wrapper'>
            <Box style={{ display: 'flex', alignItems: 'center', marginLeft: "-12px", cursor: modeType === "view" ? "not-allowed" : "pointer" }}>
              <Checkbox checked={data?.table_based_esc}
                onChange={(e) => onFieldChange('table_based_esc', e.target.checked)}
                disabled={modeType === 'view'}
                disableRipple />
              <span className="check-item-span">Enable Escalation From Table</span>
            </Box>
          </FormGroup>
        </Grid>
        : null}
      {data?.is_pbh_fixed_table || data?.fixed_rate ? null : isFcFH ? (
        <>
          {pbhTable(fhConditions, "FH")}
          {pbhTable(fcConditions, "FC")}
        </>
      ) : data?.unit_of_measurement?.value === 2 ? (
        pbhTable(fcConditions, "FC")
      ) : (
        pbhTable(fhConditions, getLabelFromValue(data?.rental_rate_based_on?.value === 8 ? data?.rental_rate_based_on?.value : data?.unit_of_measurement?.value))
      )}
      {importDetails?.modal ? <ImportModal
        requiredHeaders={requiredHeaders}
        headerMap={headerMap}
        transformExcelData={transformExcelData}
        validateExcelData={validateExcelData}
        sampleExportName={`Sample_${exportNameMap[importDetails?.data]}_${rentalRateBasedOn === 8 ? "Range_Based_Ratio" : "Based_Rate"}`}
        importDetails={importDetails}
        excelData={excelData}
        setExcelData={setExcelData}
        onImportExcel={onImportExcel}
        samplePayload={samplePayload}
        onToggleModal={() => setImportDetails({ modal: false, data: null })} /> : null}
    </>
  );
}
